import React, { useState } from 'react';
import Styles from '../BrandInsightsDashboard.module.scss';
import { FiTrendingUp, FiInstagram, FiLinkedin, FiTwitter, FiYoutube, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';

const PLATFORM_ICONS = {
  instagram: FiInstagram,
  linkedin: FiLinkedin,
  twitter: FiTwitter,
  youtube: FiYoutube,
  tiktok: FaTiktok,
};

const PLATFORM_COLORS = {
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2',
  youtube: '#FF0000',
  tiktok: '#000000',
};

const TopPerformers = ({ topPerformers, successPatterns }) => {
  const [expandedPost, setExpandedPost] = useState(null);

  if (!topPerformers || topPerformers.length === 0) {
    return (
      <div className={Styles.topPerformersSection}>
        <div className={Styles.sectionHeader}>
          <FiTrendingUp className={Styles.sectionIcon} />
          <h2>What Works For You</h2>
        </div>
        <div className={Styles.emptyCard}>
          <p>No top performers identified yet. Run analysis to see your best content.</p>
        </div>
      </div>
    );
  }

  // Extract common patterns from top performers
  const commonPatterns = extractCommonPatterns(topPerformers, successPatterns);

  return (
    <div className={Styles.topPerformersSection}>
      <div className={Styles.sectionHeader}>
        <FiTrendingUp className={Styles.sectionIcon} />
        <h2>What Works For You</h2>
      </div>

      <div className={Styles.performersGrid}>
        {/* Top Performers List */}
        <div className={Styles.performersList}>
          <h3>Your Top Performing Content</h3>
          {topPerformers.slice(0, 5).map((post, idx) => {
            const Icon = PLATFORM_ICONS[post.platform] || FiTrendingUp;
            const isExpanded = expandedPost === idx;

            return (
              <div
                key={idx}
                className={`${Styles.performerCard} ${isExpanded ? Styles.expanded : ''}`}
              >
                <div
                  className={Styles.performerHeader}
                  onClick={() => setExpandedPost(isExpanded ? null : idx)}
                >
                  <div className={Styles.performerRank}>#{idx + 1}</div>
                  <Icon
                    className={Styles.performerPlatform}
                    style={{ color: PLATFORM_COLORS[post.platform] }}
                  />
                  <div className={Styles.performerMeta}>
                    <span className={Styles.engagementBadge}>
                      {post.engagement_rate?.toFixed(1) || '?'}% engagement
                    </span>
                    {post.hook_type && (
                      <span className={Styles.hookBadge}>
                        {formatHookType(post.hook_type)}
                      </span>
                    )}
                  </div>
                  <button className={Styles.expandBtn}>
                    {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                </div>

                <p className={Styles.performerPreview}>
                  {post.content_preview || 'No preview available'}
                </p>

                {isExpanded && (
                  <div className={Styles.performerDetails}>
                    {/* Metrics */}
                    {post.metrics && (
                      <div className={Styles.metricsRow}>
                        {post.metrics.likes > 0 && (
                          <span className={Styles.metric}>{formatNumber(post.metrics.likes)} likes</span>
                        )}
                        {post.metrics.comments > 0 && (
                          <span className={Styles.metric}>{formatNumber(post.metrics.comments)} comments</span>
                        )}
                        {post.metrics.shares > 0 && (
                          <span className={Styles.metric}>{formatNumber(post.metrics.shares)} shares</span>
                        )}
                        {post.metrics.views > 0 && (
                          <span className={Styles.metric}>{formatNumber(post.metrics.views)} views</span>
                        )}
                      </div>
                    )}

                    {/* Why It Worked */}
                    {post.why_it_worked && post.why_it_worked.length > 0 && (
                      <div className={Styles.whySection}>
                        <strong>Why This Worked:</strong>
                        <ul>
                          {post.why_it_worked.map((reason, rIdx) => (
                            <li key={rIdx}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Key Elements */}
                    {post.key_elements && post.key_elements.length > 0 && (
                      <div className={Styles.keyElements}>
                        <strong>Key Elements:</strong>
                        <div className={Styles.elementChips}>
                          {post.key_elements.map((el, eIdx) => (
                            <span key={eIdx} className={Styles.elementChip}>{el}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Post Details */}
                    <div className={Styles.postDetails}>
                      {post.content_format && (
                        <span>Format: <strong>{formatFormatName(post.content_format)}</strong></span>
                      )}
                      {post.source_url && (
                        <a href={post.source_url} target="_blank" rel="noopener noreferrer" className={Styles.viewPost}>
                          View Post <FiExternalLink />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Common Patterns */}
        <div className={Styles.patternsCard}>
          <h3>Patterns Across Your Best Content</h3>
          {commonPatterns.length > 0 ? (
            <ul className={Styles.patternsList}>
              {commonPatterns.map((pattern, idx) => (
                <li key={idx} className={Styles.patternItem}>
                  <span className={Styles.patternIcon}>•</span>
                  <span>{pattern}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={Styles.noDataSmall}>Analyzing patterns...</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Extract common patterns from top performers
const extractCommonPatterns = (topPerformers, successPatterns) => {
  const patterns = [];

  // From success patterns
  if (successPatterns) {
    if (successPatterns.engagement_drivers) {
      patterns.push(...successPatterns.engagement_drivers);
    }
    if (successPatterns.format_patterns) {
      patterns.push(...successPatterns.format_patterns);
    }
  }

  // Analyze top performers
  if (topPerformers.length >= 3) {
    // Hook type analysis
    const hookCounts = {};
    topPerformers.forEach(p => {
      if (p.hook_type) {
        hookCounts[p.hook_type] = (hookCounts[p.hook_type] || 0) + 1;
      }
    });

    const topHook = Object.entries(hookCounts).sort((a, b) => b[1] - a[1])[0];
    if (topHook && topHook[1] >= 2) {
      const percent = Math.round((topHook[1] / topPerformers.length) * 100);
      patterns.push(`${percent}% use ${formatHookType(topHook[0])} hooks`);
    }

    // Format analysis
    const formatCounts = {};
    topPerformers.forEach(p => {
      if (p.content_format) {
        formatCounts[p.content_format] = (formatCounts[p.content_format] || 0) + 1;
      }
    });

    const topFormat = Object.entries(formatCounts).sort((a, b) => b[1] - a[1])[0];
    if (topFormat && topFormat[1] >= 2) {
      const percent = Math.round((topFormat[1] / topPerformers.length) * 100);
      patterns.push(`${percent}% are ${formatFormatName(topFormat[0])} format`);
    }

    // Length analysis
    const lengths = topPerformers
      .map(p => p.content_preview?.length || 0)
      .filter(l => l > 0);

    if (lengths.length >= 3) {
      const avgLength = Math.round(lengths.reduce((a, b) => a + b, 0) / lengths.length);
      if (avgLength < 100) {
        patterns.push('Short, punchy content performs best');
      } else if (avgLength < 200) {
        patterns.push('Medium-length content (100-200 chars) performs best');
      } else {
        patterns.push('Longer, detailed content performs best');
      }
    }

    // Story/personal content
    const personalCount = topPerformers.filter(p =>
      p.hook_type === 'story' ||
      p.hook_type === 'personal' ||
      p.content_preview?.toLowerCase().includes('i ') ||
      p.content_preview?.toLowerCase().includes('my ')
    ).length;

    if (personalCount >= 2) {
      patterns.push('Personal stories and experiences resonate with your audience');
    }
  }

  // Dedupe and limit
  return [...new Set(patterns)].slice(0, 8);
};

// Helpers
const formatHookType = (hookType) => {
  const formatMap = {
    'question': 'question',
    'number': 'number/list',
    'story': 'story',
    'bold_claim': 'bold claim',
    'curiosity_gap': 'curiosity gap',
    'contrarian': 'contrarian',
    'personal': 'personal'
  };
  return formatMap[hookType] || hookType?.replace(/_/g, ' ') || 'unknown';
};

const formatFormatName = (format) => {
  const formatMap = {
    'carousel': 'carousel',
    'image': 'image',
    'video': 'video',
    'reel': 'reel/short',
    'text': 'text',
    'thread': 'thread'
  };
  return formatMap[format] || format?.replace(/_/g, ' ') || 'unknown';
};

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

export default TopPerformers;
